import moment from 'moment'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import AlertOverlay from '@/components/AlertOverlay'
import Card from '@/components/Card'
import { Delete, Edit2 } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'
import Pill from '@/components/Pill'
import SpaceDeleteConfirmationOverlay from '@/pages/OrganizationSpacesPage/SpaceDeleteConfirmationOverlay'

import messages from './messages'

function OrganizationSpaces(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState({
    isActionInProgress: false,
    columns: [
      {
        property: 'name',
        header: {
          label: intl.formatMessage(messages.name),
          sortable: true
        },
        cell: {
          formatters: [
            (id: any, { rowData }: any) => (
              <>
                <span className="keyvalues__label">{rowData.name}</span>
                {rowData.default ? (
                  <Pill style={{ margin: 5 }}>{rowData.default ? intl.formatMessage(messages.defaultSpace) : ''}</Pill>
                ) : (
                  ''
                )}
              </>
            )
          ]
        }
      },
      {
        property: 'description',
        header: {
          label: intl.formatMessage(messages.description)
        },
        cell: {
          formatters: [(id: any, { rowData }: any) => <span className="sub-text">{rowData.description}</span>]
        }
      },
      {
        property: 'createdAt',
        header: {
          label: intl.formatMessage(messages.dateCreated),
          sortable: true
        },
        cell: {
          formatters: [
            (id: any, { rowData }: any) => <span className="sub-text">{moment(rowData.createdAt).fromNow()}</span>
          ]
        }
      },
      {
        property: 'id',
        cell: {
          props: {
            style: {
              width: 70,
              textAlign: 'right',
              padding: 0,
              paddingRight: '10px'
            }
          },
          // TODO Remove this onClick and wrap it in a button
          formatters: [(id: any, { rowData }: any) => <Edit2 onClick={() => props.navigateToEditSpace(rowData.id)} />]
        }
      },
      {
        property: 'id',
        cell: {
          props: {
            style: {
              width: 70,
              textAlign: 'right',
              padding: 0,
              paddingRight: '10px'
            }
          },
          formatters: [
            // TODO Remove this onClick and wrap it in a button
            (id: any, { rowData }: any) => <Delete onClick={() => showConfirmDelete(rowData.id, rowData.name)} />
          ]
        }
      }
    ], // initial columns
    isDeleting: false,
    currentPage: 1,
    spaceToDelete: undefined,
    spaceToDeleteName: undefined,
    message: undefined
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  const onDeleteSpace = async () => {
    setState({
      isDeleting: false,
      isActionInProgress: true,
      message: <FormattedMessage {...messages.deletingSpace} />
    })

    try {
      await props.onDeleteSpace(state.spaceToDelete)
    } finally {
      setState({
        spaceToDelete: '',
        isActionInProgress: false
      })
    }
  }

  const showConfirmDelete = async (spaceId: any, spaceName: any) => {
    setState({ isDeleting: true, spaceToDelete: spaceId, spaceToDeleteName: spaceName })
  }

  const onCancelDelete = async () => {
    setState({ isDeleting: false, spaceToDelete: '', spaceToDeleteName: '' })
  }

  const onSelect = (page: any) => {
    setState({ currentPage: page })
  }

  return (
    <Card isActionInProgress={state.isActionInProgress} actionMessage={state.message}>
      <Card.Content>
        {props.spaces.length > 0 ? (
          <PaginationTable
            columns={state.columns}
            pagination={{
              isClientPaging: true,
              startIndex: state.currentPage * 10,
              count: props.spaces.length,
              perPage: 10
            }}
            onPaginateSelect={onSelect}
            rows={props.spaces}
            rowItemType={<FormattedMessage {...messages.spacesLowerCase} />}
            rowKey="id"
            sortingColumn={props.sortingColumn}
          />
        ) : (
          <p className="no-users flex-center">
            <FormattedMessage {...messages.noSpaces} />
          </p>
        )}
      </Card.Content>

      {state.isDeleting && !props.spaces.find((space: any) => space.id === state.spaceToDelete).default && (
        <SpaceDeleteConfirmationOverlay
          onConfirm={onDeleteSpace}
          onCancel={onCancelDelete}
          spaceName={state.spaceToDeleteName}
        />
      )}

      {state.isDeleting && props.spaces.find((space: any) => space.id === state.spaceToDelete).default && (
        <AlertOverlay
          onDismiss={onCancelDelete}
          message={<FormattedMessage {...messages.unableToDelete} />}
          subMessage={<FormattedMessage {...messages.unableToDeleteDesc} />}
        />
      )}
    </Card>
  )
}

OrganizationSpaces.displayName = 'OrganizationSpaces'

OrganizationSpaces.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDeleteSpace: PropTypes.func.isRequired,
  navigateToEditSpace: PropTypes.func.isRequired,
  sortingColumn: PropTypes.object
}

OrganizationSpaces.defaultProps = {
  sortingColumn: { name: { direction: 'asc', position: 1 } }
}

export default OrganizationSpaces
